import dayjs from 'dayjs';
import {
  CURRENCY_TYPE_LIST,
  ECOM_ORDER_PRODUCT_STATUS_ENUM,
  ECOM_ORDER_STATUS_ENUM
} from '../../../config/constants';
import { ecomOrderExportConfig } from './ecomOrderExportConfig';

const DATA_FIELDS = {
  // ORDER
  pkEcomOrder: 'pkEcomOrder',
  status: 'status',
  createdAt: 'createdAt',

  // CHECKOUT
  currency: 'currency',
  subtotal: 'subtotal',
  discount: 'discount',
  shipping: 'shipping',
  taxes: 'taxes',
  taxesIncluded: 'taxesIncluded',
  total: 'total',

  // CUSTOMER
  customerName: 'customerName',
  customerEmail: 'customerEmail',

  // PRODUCT
  itemQuantity: 'itemQuantity',
  itemName: 'itemName',
  itemOptions: 'itemOptions',
  itemProperties: 'itemProperties',
  itemPrice: 'itemPrice',
  itemTotalDiscount: 'itemTotalDiscount',
  itemTotalPrice: 'itemTotalPrice',
  itemSKU: 'itemSKU',
  itemRequiresShipping: 'itemRequiresShipping',
  itemTaxable: 'itemTaxable',
  itemVendor: 'itemVendor'
};

const EXPORT_ORDER_FIELDS = [
  {
    label: 'Order ID',
    selectDisabled: true,
    exportColumns: [{ dataValue: DATA_FIELDS.pkEcomOrder }],
    getExportValue: ({ regCartOrder, index }) => ({
      [DATA_FIELDS.pkEcomOrder]: index === 0 ? regCartOrder.pkRegCart : ''
    })
  },
  {
    label: 'Customer name',
    exportColumns: [{ dataValue: DATA_FIELDS.customerName }],
    getExportValue: ({ regCartOrder, index }) => ({[DATA_FIELDS.customerName]: index === 0 ? regCartOrder.user.name : ''})
  },
  {
    label: 'Customer email',
    selectDisabled: true,
    exportColumns: [{ dataValue: DATA_FIELDS.customerEmail }],
    getExportValue: ({ regCartOrder, index }) => ({[DATA_FIELDS.customerEmail]: index === 0 ? regCartOrder.user.email : ''})
  },
  {
    label: 'Status',
    selectDisabled: true,
    exportColumns: [{ dataValue: DATA_FIELDS.status }],
    getExportValue: ({
      regCartOrder,
      ecomOrder,
      payments,
      products,
      productType,
      index
    }) => {
      if (index !== 0) {
        return { [DATA_FIELDS.status]: '' };
      }

      const status = null;

      return { [DATA_FIELDS.status]: status };
    }
  },
  {
    label: 'Created at',
    exportColumns: [{ dataValue: DATA_FIELDS.createdAt }],
    getExportValue: ({ payments, index }) => ({
      [DATA_FIELDS.createdAt]:
        index === 0
          ? dayjs(payments[0].purchasedAt).format('MMMM D, YYYY h:mm A')
          : ''
    })
  }
];

const EXPORT_CHECKOUT_FIELDS = [
  {
    label: 'Currency',
    exportColumns: [{ dataValue: DATA_FIELDS.currency }],
    getExportValue: ({ payments, index }) => ({
      [DATA_FIELDS.currency]:
        index === 0
          ? CURRENCY_TYPE_LIST.find(
              (c) =>
                c.value.toString() === payments[0]?.fkCurrencyType?.toString()
            )?.abbr
          : ''
    })
  },
  {
    label: 'Subtotal',
    exportColumns: [{ dataValue: DATA_FIELDS.subtotal }],
    getExportValue: ({ payments }, index) => ({
      [DATA_FIELDS.subtotal]:
        index === 0
          ? (payments[0].amount -
              (payments[0].ecomStoreCartPaymentFee?.tieredPricingAmount || 0)) /
            100
          : ''
    })
  },
  {
    label: 'Discount',
    exportColumns: [{ dataValue: DATA_FIELDS.discount }],
    getExportValue: ({
      regCartOrder,
      ecomOrder,
      payments,
      products,
      productType,
      index
    }) => ({ [DATA_FIELDS.discount]: index === 0 ? 0 : '' })
  },
  {
    label: 'Shipping',
    exportColumns: [{ dataValue: DATA_FIELDS.shipping }],
    getExportValue: ({
      regCartOrder,
      ecomOrder,
      payments,
      products,
      productType,
      index
    }) => ({
      [DATA_FIELDS.shipping]:
        index === 0
          ? (order.ecomOrderPaymentShippingFees?.reduce(
              (r, c) => r + c.shippingCost,
              0
            ) || 0) / 100
          : ''
    })
  },
  {
    label: 'Taxes',
    exportColumns: [{ dataValue: DATA_FIELDS.taxes }],
    getExportValue: ({ order, payment, products, isVendorView }, index) => ({[DATA_FIELDS.taxes]: index === 0 && !isVendorView ? 0 : 0})
  },
  {
    label: 'Taxes included',
    exportColumns: [{ dataValue: DATA_FIELDS.taxesIncluded }],
    getExportValue: ({ order, payment, products, isVendorView }, index) => ({[DATA_FIELDS.taxesIncluded]: index === 0 || isVendorView ? 'NO' : ''})
  },
  {
    label: 'Tiered pricing',
    isStoreOnly: true,
    exportColumns: [{ dataValue: DATA_FIELDS.tieredPricing }],
    getExportValue: ({
      regCartOrder,
      ecomOrder,
      payments,
      products,
      productType,
      index
    }) => ({
      [DATA_FIELDS.tieredPricing]:
        index === 0
          ? payment?.ecomStoreCartPaymentFee?.tieredPricingAmount || 0
          : ''
    })
  },
  {
    label: 'Total',
    exportColumns: [{ dataValue: DATA_FIELDS.total }],
    getExportValue: ({ payments }, index) => ({[DATA_FIELDS.total]: index === 0 ? payments[0].amount / 100 : ''})
  }
];

const EXPORT_PRODUCT_FIELDS = [
  {
    label: 'Item quantity',
    exportColumns: [{ dataValue: DATA_FIELDS.itemQuantity }],
    getExportValue: ({
      regCartOrder,
      ecomOrder,
      payments,
      products,
      productType,
      index
    }) => ({[DATA_FIELDS.itemQuantity]: products.reduce((r, c) => r + c.count, 0)})
  },
  {
    label: 'Item name',
    exportColumns: [{ dataValue: DATA_FIELDS.itemName }],
    getExportValue: ({
      regCartOrder,
      ecomOrder,
      payments,
      products,
      productType,
      index
    }) => ({ [DATA_FIELDS.itemName]: products[0].productName })
  },
  {
    label: 'Item options',
    exportColumns: [{ dataValue: DATA_FIELDS.itemOptions }],
    getExportValue: ({
      regCartOrder,
      ecomOrder,
      payments,
      products,
      productType,
      index
    }) => ({
      [DATA_FIELDS.itemOptions]: products[0].ecomVendorProductVariantOptions
        .map((op) => op.value)
        .join(' / ')
    })
  },
  {
    label: 'Item properties',
    exportColumns: [{ dataValue: DATA_FIELDS.itemProperties }],
    getExportValue: ({
      regCartOrder,
      ecomOrder,
      payments,
      products,
      productType,
      index
    }) => ({
      [DATA_FIELDS.itemProperties]: products[0].ecomOrderProductInputAnswers
        .map((op) => `${op.label}: ${op.value}`)
        .join('\n')
    })
  },
  {
    label: 'Item price',
    exportColumns: [{ dataValue: DATA_FIELDS.itemPrice }],
    getExportValue: ({
      regCartOrder,
      ecomOrder,
      payments,
      products,
      productType,
      index
    }) => ({
      [DATA_FIELDS.itemPrice]:
        products.reduce((r, c) => r + (c.productCost + c.addonCost), 0) / 100
    })
  },
  {
    label: 'Item total discount',
    exportColumns: [{ dataValue: DATA_FIELDS.itemTotalDiscount }],
    getExportValue: ({
      regCartOrder,
      ecomOrder,
      payments,
      products,
      productType,
      index
    }) => ({ [DATA_FIELDS.itemTotalDiscount]: '0' })
  },
  {
    label: 'Item total price',
    exportColumns: [{ dataValue: DATA_FIELDS.itemTotalPrice }],
    getExportValue: ({
      regCartOrder,
      ecomOrder,
      payments,
      products,
      productType,
      index
    }) => ({
      [DATA_FIELDS.itemTotalPrice]:
        products.reduce(
          (r, c) => r + (c.productCost + c.addonCost) * c.count,
          0
        ) / 100
    })
  },
  {
    label: 'Item SKU',
    exportColumns: [{ dataValue: DATA_FIELDS.itemSKU }],
    getExportValue: ({
      regCartOrder,
      ecomOrder,
      payments,
      products,
      productType,
      index
    }) => ({ [DATA_FIELDS.itemSKU]: products[0].variantSku })
  },
  {
    label: 'Item requires shipping',
    exportColumns: [{ dataValue: DATA_FIELDS.itemRequiresShipping }],
    getExportValue: ({
      regCartOrder,
      ecomOrder,
      payments,
      products,
      productType,
      index
    }) => ({ [DATA_FIELDS.itemRequiresShipping]: 'YES' })
  },
  {
    label: 'Item taxable',
    exportColumns: [{ dataValue: DATA_FIELDS.itemTaxable }],
    getExportValue: ({
      regCartOrder,
      ecomOrder,
      payments,
      products,
      productType,
      index
    }) => ({ [DATA_FIELDS.itemTaxable]: 'NO' })
  },
  {
    label: 'Item vendor',
    exportColumns: [{ dataValue: DATA_FIELDS.itemVendor }],
    getExportValue: ({
      regCartOrder,
      ecomOrder,
      payments,
      products,
      productType,
      index
    }) => ({ [DATA_FIELDS.itemVendor]: products[0].vendorName })
  }
];

const regCartOrderExportConfig = {
  fieldSections: [
    {
      name: 'Order',
      value: 'ORDER',
      fields: EXPORT_ORDER_FIELDS
    },
    {
      name: 'Checkout',
      value: 'CHECKOUT',
      fields: EXPORT_CHECKOUT_FIELDS
    },
    {
      name: 'Product',
      value: 'PRODUCT',
      fields: EXPORT_PRODUCT_FIELDS
    }
  ],
  getCsvData: (regCartOrders = []) =>
    regCartOrders.flatMap((regCart) => {
      const ecomOrderData = regCart.ecomOrder
        ? ecomOrderExportConfig.getCsvData([regCart.ecomOrder])
        : null;

      const payments = [];

      return regCart.regCartProducts.map((p, index) => ({
        regCartOrder: regCart,
        ecomOrder: ecomOrderData,
        payments,
        products: [p],
        productType: 'REG_CART_PRODUCT',
        index
      }));
    })
};

export { regCartOrderExportConfig };
